import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import {
  useSupportAgentAssign,
  useSupportAgentRename,
  useSupportAgentSend,
  useSupportAgentSetStatus,
} from '@/api/support'
import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { type SupportAgentOption } from '@/components/organisms/SupportAgentDetails'
import { type SupportEscouadeFilter, type SupportQueueFilter } from '@/components/organisms/SupportAgentQueue'
import { type SupportConversationItem } from '@/components/organisms/SupportConversation'
import { type SupportAgentViewProps } from '@/components/templates/SupportAgentView'
import { useNow } from '@/hooks/useNow'
import {
  agentQueueFilterFor,
  agentQueueFilterOf,
  matchesQueueFilter,
  searchedNumberOf,
  selectAgentQueue,
} from '@/state/support/agentQueue'
import {
  agentSupportRequestItemsQuery,
  escouadeAgentsQuery,
  piloteUserAccountQuery,
  supportRequestAgentQuery,
  supportRequestQuery,
} from '@/state/support/supportRequests'
import {
  useAgentSupportRequests,
  useSupportAgentSoundPreference,
  useSupportAgentUnreadCount,
} from '@/state/support/useAgentSupportRequests'
import { useSupportReads } from '@/state/support/useSupportReads'

import { useBackToPilote } from '../actions/useBackToPilote'

import { isSupportObjectUrl } from './embed/piloteFrame'
import { agentThreadItem, fullName, queueRow } from './supportAgentItems'
import { useSupportLabels } from './useSupportLabels'

const routeApi = getRouteApi('/soutien/')

type Intl = ReturnType<typeof useIntl>

const agentLabels = (
  agents: ReadonlyArray<{ piloteUserId: number; firstName: string; lastName: string }>,
  me: number | null,
  intl: Intl
): SupportAgentOption[] => {
  const sharedFirstNames = new Set(
    agents.map(({ firstName }) => firstName).filter((firstName, index, names) => names.indexOf(firstName) !== index)
  )
  return [...agents]
    .sort(
      (a, b) => Number(b.piloteUserId === me) - Number(a.piloteUserId === me) || a.firstName.localeCompare(b.firstName)
    )
    .map((agent) => ({
      piloteUserId: agent.piloteUserId,
      label:
        agent.piloteUserId === me
          ? intl.formatMessage({ id: 'soutien.details.escouade.self' }, { firstName: agent.firstName })
          : sharedFirstNames.has(agent.firstName)
            ? fullName(agent)
            : agent.firstName,
    }))
}

export const useSupportAgentPage = (): SupportAgentViewProps => {
  const intl = useIntl()
  const labels = useSupportLabels()
  const now = useNow()
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const { firebaseUser, piloteUserId, firstName, supportRole } = useAuthenticatedFirebaseUser()
  const line = supportRole ?? 'l1'
  const { uid } = firebaseUser
  const { requests, loadState, retry } = useAgentSupportRequests()
  const { isLoaded: readsLoaded, isUnreadByAgent, lastReadAt, markRead } = useSupportReads()
  const unreadCount = useSupportAgentUnreadCount(requests)
  const [soundEnabled, setSoundEnabled] = useSupportAgentSoundPreference()
  const backToPilote = useBackToPilote()

  const escouade = line === 'l1' ? (search.escouade ?? 'all') : 'all'
  const query = search.q ?? ''
  const selected = search.n ?? null
  const searched = useQuery(supportRequestAgentQuery(searchedNumberOf(query)))
  const agentDocument = useQuery(supportRequestAgentQuery(selected)).data?.data

  // A direct link (`/soutien?n=1042`) names no filter: the list opens on the one holding the
  // request, then pins it in the URL so a later status change does not move the list.
  const selectedStatus = agentDocument?.status
  const linkedFilter =
    search.filter === undefined && query === '' && selectedStatus !== undefined
      ? agentQueueFilterFor(line, selectedStatus)
      : undefined
  const filter = agentQueueFilterOf(line, search.filter ?? linkedFilter)

  const updateSearch = useCallback(
    (update: { filter?: SupportQueueFilter; escouade?: SupportEscouadeFilter; q?: string; n?: number }) => {
      handleNotAwaitedPromise(navigate({ search: (previous) => ({ ...previous, ...update }), replace: true }))
    },
    [navigate]
  )

  useEffect(() => {
    if (linkedFilter !== undefined) {
      updateSearch({ filter: linkedFilter })
    }
  }, [linkedFilter, updateSearch])

  const queueRows = useMemo(
    () =>
      selectAgentQueue(requests, { filter, escouade, query, searched: searched.data?.data, me: piloteUserId }).map(
        (request) => queueRow(request, { line, me: piloteUserId, unread: isUnreadByAgent(request), intl, labels, now })
      ),
    [requests, filter, escouade, query, searched.data, line, piloteUserId, isUnreadByAgent, labels, intl, now]
  )

  const counts = useMemo(() => {
    const count = (queueFilter: SupportQueueFilter) =>
      requests.filter(({ status }) => matchesQueueFilter(queueFilter, status)).length
    return { toHandle: count('toHandle'), waiting: count('waiting'), valian: count('valian'), open: count('open') }
  }, [requests])

  const request = useQuery(supportRequestQuery(selected)).data?.data
  const items = useQuery(agentSupportRequestItemsQuery(selected))
  const author = useQuery(piloteUserAccountQuery(request?.author.piloteUserId ?? null))
  const escouadeAgents = useQuery(escouadeAgentsQuery())

  const [opened, setOpened] = useState<{ number: number | null; readUpTo: Date | null }>({
    number: null,
    readUpTo: null,
  })
  if (readsLoaded && opened.number !== selected) {
    setOpened({ number: selected, readUpTo: selected === null ? null : lastReadAt(selected) })
  }

  const openedReadUpTo = opened.number === selected ? opened.readUpTo : null
  const lastActivityAt = agentDocument?.lastActivityAt
  useEffect(() => {
    if (readsLoaded && selected !== null && lastActivityAt) {
      markRead(selected, lastActivityAt)
    }
  }, [readsLoaded, selected, lastActivityAt, markRead])

  useEffect(() => {
    const previous = document.title
    document.title = intl.formatMessage({ id: 'soutien.agent.tabTitle' }, { count: unreadCount })
    return () => {
      document.title = previous
    }
  }, [intl, unreadCount])

  const send = useSupportAgentSend()
  const setStatus = useSupportAgentSetStatus()
  const assign = useSupportAgentAssign()
  const rename = useSupportAgentRename()
  const failedOn = (mutation: { isError: boolean; variables?: { number: number } }) =>
    mutation.isError && mutation.variables?.number === selected

  const threadItems = useMemo(
    () =>
      [...(items.data?.data ?? [])]
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        .map((item) => agentThreadItem(item, { line, uid, readUpTo: openedReadUpTo, intl }))
        .filter((item): item is SupportConversationItem => item !== null),
    [items.data, line, uid, openedReadUpTo, intl]
  )

  const agentOptions = useMemo(() => {
    const activeAgents = (escouadeAgents.data?.data ?? []).filter(({ active }) => active)
    const assignee = agentDocument?.assignee
    const withAssignee =
      assignee && !activeAgents.some(({ piloteUserId: id }) => id === assignee.piloteUserId)
        ? [...activeAgents, assignee]
        : activeAgents
    return agentLabels(withAssignee, piloteUserId, intl)
  }, [escouadeAgents.data, agentDocument?.assignee, piloteUserId, intl])

  const signature = intl.formatMessage({ id: 'soutien.escouadeSignature' }, { firstName: firstName ?? '' })
  const authorAccount = author.data?.data

  return {
    agentLabel:
      line === 'l1' ? signature : intl.formatMessage({ id: 'soutien.valianSignature' }, { firstName: firstName ?? '' }),
    soundEnabled,
    onSoundToggled: () => {
      setSoundEnabled((enabled) => !enabled)
    },
    onBackToPilote: () => {
      handleNotAwaitedPromise(backToPilote())
    },
    queue: {
      line,
      filter,
      counts,
      escouade,
      search: query,
      rows: queueRows,
      selected: selected ?? undefined,
      state: loadState,
      onFilterChange: (value) => {
        updateSearch({ filter: value })
      },
      onEscouadeChange: (value) => {
        updateSearch({ escouade: value })
      },
      onSearchChange: (value) => {
        updateSearch({ q: value === '' ? undefined : value })
      },
      onSelect: (number) => {
        updateSearch({ n: number })
      },
      onRetry: retry,
    },
    conversation:
      selected !== null && request && agentDocument
        ? {
            number: selected,
            meta: [
              `#${request.number}`,
              request.centralName,
              labels.category(request.category),
              ...(request.blocking === 'canContinue'
                ? []
                : [intl.formatMessage({ id: `soutien.blocking.short.${request.blocking}` })]),
            ].join(' · '),
            title: request.title,
            rename:
              line === 'l1'
                ? {
                    saving: rename.isPending,
                    error: failedOn(rename),
                    onRename: async (title) => {
                      try {
                        await rename.mutateAsync({ number: selected, title })
                        return true
                      } catch {
                        return false
                      }
                    },
                  }
                : undefined,
            items: threadItems,
            summary: {
              pending: agentDocument.summaryPending ?? false,
              summary: agentDocument.summary
                ? {
                    time: intl.formatTime(agentDocument.summary.generatedAt),
                    request: agentDocument.summary.request,
                    blocking: agentDocument.summary.blocking,
                    done: agentDocument.summary.done,
                    next: agentDocument.summary.next,
                  }
                : null,
            },
            composer: {
              line,
              status: agentDocument.status,
              signature,
              sending: send.isPending,
              error: failedOn(send),
              onSend: async ({ mode, text, afterStatus }) => {
                try {
                  await send.mutateAsync({ number: selected, mode, text, afterStatus })
                  return true
                } catch {
                  return false
                }
              },
            },
          }
        : null,
    details:
      selected !== null && request && agentDocument
        ? {
            line,
            status: agentDocument.status,
            assigneeId: agentDocument.assignee?.piloteUserId ?? null,
            agents: agentOptions,
            requester: {
              name: fullName(request.author),
              permissions: labels.permissions(authorAccount?.permissions ?? request.context.permissions),
              central: request.centralName,
              email: authorAccount ? authorAccount.email : '',
            },
            context: {
              ...request.context,
              object: request.context.object && {
                label: request.context.object.label,
                url: isSupportObjectUrl(request.context.object.url) ? request.context.object.url : null,
              },
            },
            linearTicket: agentDocument.linearTicket,
            saving: setStatus.isPending || assign.isPending,
            error: failedOn(setStatus) || failedOn(assign),
            onStatusChange: (status) => {
              setStatus.mutate({ number: selected, status })
            },
            onAssigneeChange: (assigneeId) => {
              assign.mutate({ number: selected, piloteUserId: assigneeId })
            },
          }
        : null,
  }
}
